<template>
  <Teleport :disabled="!isComponentActive && !isPersist" to="body" tag="div" v-if="isDisplay">
    <div
      class="fixed top-0 left-0 w-full h-full flex max-h"
      :class="[
        getStyle(styles, 'container'),
        ...(isInvisible ? [ 'pointer-events-none invisible' ] : [])
      ]"
      :style="{
        zIndex,
        paddingTop: (isRight && !isCloseButton) || isFullscreen ? '0' : (layout === 'display' ?
          'max(var(--inset-top), 16px)' :
          'var(--inset-top)'),
        paddingBottom: layout === 'display' ?
          (kHeight > 0 ? `${kHeight}px` : `max(var(--inset-bottom), 16px)`) :
          `${kHeight ?? 0}px`
      }"
    >
      <div
        class="bg-cover transition fixed w-full h-full left-0 top-0" :class="[
          isGradient ? '' : 'bg-[#202020] dark:bg-dark-600',
          isShow ? 'duration-200' : 'duration-200 delay-100',
          isClosable ? '' : 'pointer-events-none'
        ]"
        :style="{
          opacity: isShow ? opacity : 0,
          ...(isGradient ? { backgroundImage: backgroundGradient } : {})
        }"
        @click="onClose"
      />

      <div
        class="w-full text-right px-4 sm:px-0" 
        :class="[
          getStyle(styles, 'body', [ 'size' ]),
          getStyle(styles, 'close'),
          isCloseButton ? 'py-4' : 'py-1',
          isShow ? '' : 'opacity-0',
          isOuterClose ? '' : 'sm:hidden'
        ]"
        v-if="!((!isCloseButton && isRight) || isFullscreen)"
      >
        <ButtonBase
          style-type="round"
          color="white"
          size="sm"
          @click="onClose"
          v-if="isClosable && isCloseButton"
        >
          <IconBase :width="20" name="cute/cross" />
        </ButtonBase>
      </div>
      
      <div
        class="sm:shadow-md relative w-full flex flex-col overflow-hidden transition-all duration-200"
        :class="[
          isGrow ? 'grow' : '',
          isShow ? 'ease-out' : isRight && !isCloseButton ? 'opacity-0 translate-x-full' : (layout === 'base' ? 'opacity-0 translate-y-full sm:translate-y-4 ease-in' : 'opacity-0 scale-95 ease-in'),
          isDisplay ? 'top-auto left-auto' : '-top-[-9999px] -left-[-9999px]',
          getStyle(styles, 'body'),
          (isRight && !isCloseButton) || isFullscreen ? '!rounded-none sm:!rounded-3xl' : '',
        ]"
        :style="{
          ...(theme ? { backgroundColor: theme.base.background.weak } : {}),
        }"
      >
        <ButtonBase
          class="!absolute right-4 sm:top-4 z-40"
          :class="[
            isFloatingClose ? '!top-inset-top mt-7' : '',
            layout === 'base' && !isFloatingClose ? (isOuterClose ? 'hidden' : 'hidden sm:flex') : 'top-4'
          ]"
          style-type="round"
          size="sm"
          color="white"
          @click="onClose"
          v-if="isClosable && isCloseButton"
        >
          <IconBase :width="18" name="cute/cross" />
        </ButtonBase>

        <div class="font-bold text-md shrink-0 border-b border-charcoal-100" v-if="$slots.header">
          <div class="flex items-center h-16 px-3 space-x-2">
            <button class="relative z-10 bg-charcoal-50 active:bg-charcoal-200 rounded-full h-8 w-8 flex items-center justify-center" type="button" v-if="isBackable" @click="emit('back')">
              <IconBase name="cute/arrow-left-fill" :width="18" />
            </button>

            <slot name="header" />
          </div>
          <div>
            <slot name="header-below" />
          </div>
        </div>
        
        <slot name="body" v-if="overflow === 'hidden'" />
        <div class="grow overflow-auto overscroll-none" :class="[ $slots.footer || layout === 'display' ? '' : (isNoInsets ? '' : 'pb-inset-bottom') ]" v-else-if="$slots.body" ref="$body">
          <slot :scroll-ref="$body" name="body"></slot>
        </div>

        <div class="md:pb-0" :class="[ getStyle(styles, 'footer'), isNoInsets ? '' : 'pb-inset-bottom' ]" v-if="$slots.footer">
          <slot name="footer"></slot>
        </div>
      </div>

      <div class="absolute bottom-inset-bottom right-0 p-4 z-20" v-if="isScrollUp">
        <Transition name="slide-up">
          <ButtonBase style-type="round" color="white" @click="onScrollUp" v-if="isShowUpButton">
            <IconBase name="cute/arrow-left" :width="24" class="rotate-90" />
          </ButtonBase>
        </Transition>
      </div>

    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { defineStyles, getStyle } from '@/utils/useStyles'
import WindowStore from '@/store/window'
import PopinStore from '@/store/popin'
import UserStore from '@/store/user'
import { getKeyboard, isNoInsets } from '@/utils/native/useKeyboard'
import gradient from '@/assets/img/gradients/hard/gradient-1.jpg'
import gradientDark from '@/assets/img/gradients/hard/gradient-15.jpg'
import { Theme } from '@/classes'

const props = withDefaults(defineProps<{
  isActive: boolean
  isFull?: boolean
  isFullscreen?: boolean
  isRight?: boolean
  isGradient?: boolean
  isAutoClose?: boolean
  isPersist?: boolean
  isScrollUp?: boolean
  isClosable?: boolean
  isCloseButton?: boolean
  isBackable?: boolean
  isMobileFixed?: boolean
  isDelayedClose?: boolean
  theme?: Theme | null
  isOuterClose?: boolean
  isFloatingClose?: boolean
  isGrow?: boolean
  classesFooter?: string
  color?: 'white' | 'ruby' | 'tiger' | 'royal' | 'whale' | 'flamingo' | 'charcoal' | 'urchin' | 'purple'
  overflow?: 'auto' | 'hidden'
  layout?: 'base' | 'display'
  size?: string
  opacity?: number
  offset?: number
  z?: number
}>(), {
  layout: 'base',
  isOuterClose: false,
  isFloatingClose: false,
  isFull: false,
  isFullscreen: false,
  isPersist: false,
  isGrow: false,
  isScrollUp: false,
  isRight: false,
  theme: null,
  isGradient: false,
  isBackable: false,
  overflow: 'auto',
  classesFooter: '',
  color: 'white',
  isClosable: true,
  isCloseButton: true,
  isAutoClose: true,
  offset: 600,
  opacity: 0.9,
  z: 200
})

const route = useRoute()

const { isActive } = toRefs(props)
const isShow = ref(false)
const isDisplay = ref(false)

const popinStore = PopinStore()
const userStore = UserStore()
const { colorTheme } = storeToRefs(userStore)
const { kHeight } = getKeyboard()
const { lockWindow } = WindowStore()
const windowStore = WindowStore()
const { isInvisible } = storeToRefs(windowStore)
const { popinZIndex } = storeToRefs(popinStore)
const zIndex = ref(200)

const backgroundGradient = computed(() => colorTheme.value === 'dark' ? `url(${gradientDark})` : `url(${gradient})`)

watch(isActive, () => {
  if (isActive.value === true) {
    isDisplay.value = true
    popinZIndex.value++
    zIndex.value = popinZIndex.value

    setTimeout(() => {
      lockWindow(true)
      isShow.value = true
    }, 10)
  } else {
    if (isShow.value) {
      lockWindow(false)
      popinZIndex.value--
    }
    
    isShow.value = false

    setTimeout(() => {
      if (isActive.value) return
      isDisplay.value = false
    }, 300)
  }
}, { immediate: true })

const routeName = ref()

watch(() => route, () => {
  if (props.isAutoClose && isActive.value && routeName.value && routeName.value !== route.name) {
    lockWindow(false)

    onClose()
  }

  routeName.value = route.name
}, { immediate: true })

const onClose = () => {
  emit('close')

  // isShow.value = false
  // setTimeout(() => emit('close'), props.isDelayedClose ? 300 : 10)
}

const styles = computed(() => defineStyles({
  container: {
    layout: {
      base: 'flex-col items-center justify-end sm:justify-center',
      display: 'px-4 items-end justify-end'
    }
  },
  close: {
    layout: {
      base: '',
      display: 'hidden'
    }
  },
  body: {
    isMobileFixed: [
      'h-full md:h-auto max-h-none md:max-h-5/6 md:max-h-[90dvh] md:rounded-3xl',
      'max-h-5/6 max-h-[90dvh] rounded-t-3xl sm:rounded-3xl'
    ],
    size: {
      '4xs': 'sm:max-w-[350px]',
      '3xs': 'sm:max-w-md',
      '2xs': 'sm:max-w-lg',
      'xs': 'sm:max-w-wrapper-xs',
      'sm': 'sm:max-w-wrapper-sm',
      'base': 'sm:max-w-wrapper',
    },
    layout: {
      base: '',
      display: 'rounded-3xl w-auto'
    },
    color: {
      white: 'bg-light-50 dark:bg-dark-920 dark:border dark:border-dark-700 dark:text-dark-50',
      whale: 'bg-whale-950',
      tiger: 'bg-tiger-50',
      royal: 'bg-royal-100 dark:bg-dark-920 dark:border dark:border-dark-700 dark:text-dark-50',
      urchin: 'bg-urchin-100 dark:bg-dark-920 dark:text-dark-50',
      ruby: 'bg-ruby-100',
      flamingo: 'bg-flamingo-50 dark:bg-dark-920 dark:text-dark-50',
      charcoal: 'bg-charcoal-950',
      purple: 'bg-gradient-to-t from-royal-800/10 via-royal-800/20 to-urchin-700 sm:border border-urchin-700'
    },
    isFull: ['h-full max-h-full sm:bottom-6 sm:top-6 sm:h-auto sm:absolute', '' ],
    isFullscreen: ['h-full max-h-full sm:bottom-6 sm:top-6 sm:h-auto sm:absolute', '' ],
    isRight: ['sm:right-4', '' ]
  },
  footer: {
    color: {
      white: 'bg-light-200 dark:bg-dark-800 border-t border-light-300 dark:border-dark-600',
      whale: 'bg-whale-950',
      tiger: 'bg-tiger-50',
      royal: 'bg-royal-100 dark:bg-dark-920 dark:border dark:border-dark-700 dark:text-dark-50',
      urchin: 'bg-urchin-200 dark:bg-dark-800 border-t border-urchin-300 dark:border-dark-600',
      ruby: 'bg-ruby-100',
      flamingo: 'bg-flamingo-50 dark:bg-dark-920 dark:text-dark-50',
      charcoal: 'bg-charcoal-950',
      purple: 'bg-gradient-to-t from-royal-800/10 via-royal-800/20 to-urchin-700 sm:border border-urchin-700'
    },
  }
}, props))

// END EVENT
const $body = ref()
const { y: scrollY, arrivedState, directions } = useScroll($body, {
  offset: { bottom: props.offset }
})

const directionUp = ref(false)
const isShowUpButton = computed(() => props.isScrollUp ? scrollY.value > 100 && directionUp.value : false)

watch(directions, () => {
  if (!props.isScrollUp) return
  if (directions.top) directionUp.value = true
  if (directions.bottom) directionUp.value = false
})

const onScrollUp = () => {
  scrollY.value = 0
}

watch(() => arrivedState.bottom, (v) => {
  if (isComponentActive.value && v) emit('end')
})

const emit = defineEmits([ 'close', 'end', 'back'])

// HOOKS

const { isComponentActive } = useKeepAlive({
  onLeave: () => {
    lockWindow(false)
  }
})

defineExpose({
  $body
})

</script>